// Canonical Organization structured data for the whole site.
//
// This lives in code (not a Builder.io block) on purpose: Builder content
// rendered through the React wrapper does not server-render, which is why
// direct page fetches (Googlebot, the LLM crawlers D&D tested) could not see
// the homepage's rich schema even though it appeared in the rendered DOM.
// Emitting the @graph from the SSR'd <Head> guarantees it is in the initial
// HTML on all 43 pages.

const SITE = 'https://prismatic.io';

export const ORGANIZATION_ID = `${SITE}/#organization`;
const LOGO_ID = `${SITE}/#logo`;
const WEBSITE_ID = `${SITE}/#website`;

interface G2Rating {
  ratingValue: string;
  reviewCount: string;
  url?: string;
}

// Editable override sourced from Builder (the `footers` global): marketing can
// update the review count weekly without a deploy. Any missing field falls back
// to the default below.
export type G2RatingOverride = { ratingValue?: string | null; reviewCount?: string | null } | null | undefined;

// G2 aggregate rating defaults, sourced from the live G2 profile. The rating
// value rarely moves; the review count drifts weekly and is meant to be
// overridden from Builder. Keep these in sync as a fallback: structured data
// must match what G2 displays or Google flags it as inconsistent.
const DEFAULT_G2_RATING: G2Rating = {
  ratingValue: '4.8',
  reviewCount: '231',
  url: 'https://www.g2.com/products/prismatic/reviews',
};

const resolveG2Rating = (override?: G2RatingOverride): G2Rating => ({
  ...DEFAULT_G2_RATING,
  ...(override?.ratingValue ? { ratingValue: String(override.ratingValue) } : {}),
  ...(override?.reviewCount ? { reviewCount: String(override.reviewCount) } : {}),
});

/**
 * Reads the optional G2 rating override off a Builder data object (e.g.
 * `footerContent.data`). Returns null when neither field is present.
 */
export const extractG2Override = (
  source?: { g2RatingValue?: unknown; g2ReviewCount?: unknown } | null,
): G2RatingOverride => {
  if (!source) {
    return null;
  }
  const asString = (value: unknown) =>
    typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const ratingValue = asString(source.g2RatingValue);
  const reviewCount = asString(source.g2ReviewCount);

  if (!ratingValue && !reviewCount) {
    return null;
  }

  return { ratingValue: ratingValue || null, reviewCount: reviewCount || null };
};

interface AggregateRating {
  '@type': 'AggregateRating';
  ratingValue: string;
  reviewCount: string;
  bestRating: string;
  worstRating: string;
  url?: string;
}

export const buildAggregateRating = (override?: G2RatingOverride): AggregateRating => {
  const rating = resolveG2Rating(override);

  return {
    '@type': 'AggregateRating',
    ratingValue: rating.ratingValue,
    reviewCount: rating.reviewCount,
    bestRating: '5',
    worstRating: '1',
    ...(rating.url ? { url: rating.url } : {}),
  };
};

/**
 * Builds the canonical Organization (+ optional WebSite) `@graph`.
 *
 * Organization is emitted sitewide (referenced by `@id`), which is valid and is
 * what restores crawler/LLM visibility of the homepage's rich schema once it is
 * SSR'd from code rather than a Builder block.
 *
 * The WebSite node, however, is a homepage-only entity by convention: every SEO
 * tool (Yoast et al.) emits it once, on the site root. Duplicating it across
 * every inner page is what a structured-data scanner flags ("this script on the
 * Homepage only"). So it is gated behind `includeWebSite`, which the <Head>
 * sets true only on the homepage.
 */
export const getOrganizationSchema = (g2Override?: G2RatingOverride, { includeWebSite = false } = {}) => {
  const aggregateRating = buildAggregateRating(g2Override);

  const organization = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Prismatic',
    url: SITE,
    logo: {
      '@type': 'ImageObject',
      '@id': LOGO_ID,
      url: `${SITE}/prismatic-logo.svg`,
      contentUrl: `${SITE}/prismatic-logo.svg`,
      width: 140,
      height: 30,
      caption: 'Prismatic - The integration platform for B2B SaaS teams',
      inLanguage: 'en-US',
    },
    image: { '@id': LOGO_ID },
    description:
      'Prismatic is the integration platform for B2B SaaS teams, enabling them to build, deploy, and support native integrations.',
    sameAs: [
      'https://www.linkedin.com/company/prismatic-io/',
      'https://x.com/prismatic_io',
      'https://github.com/prismatic-io/',
    ],
    aggregateRating,
  };

  const website = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE,
    name: 'Prismatic',
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: 'en-US',
  };

  return {
    '@context': 'https://schema.org',
    '@graph': includeWebSite ? [organization, website] : [organization],
  };
};
